import type { ReviewFormDto } from '@flexbyt/shared';
import { site } from './site.js';

/**
 * The hosted review form, at `/f/:slug`.
 *
 * ---------------------------------------------------------------------------
 * Why this page lives on the marketing site
 * ---------------------------------------------------------------------------
 * It is the one page in the product a customer's *customer* ever sees — opened
 * from an email, a QR code on a receipt, a link on a thank-you page. It has no
 * session, no navigation, and nothing to do with the dashboard, so it belongs
 * on the origin people already reach without logging in. `formUrl` in the
 * shared package builds the link from `FORM_BASE_URL`, and this is the route it
 * points at.
 *
 * ---------------------------------------------------------------------------
 * One request, on the server
 * ---------------------------------------------------------------------------
 * `GET /api/public/forms/:slug` answers with the form, the workspace's plan and
 * the business's name in a single response, because the legacy page made four
 * calls to draw one screen and felt like it on a phone. It is fetched in the
 * route loader rather than after hydration so the business's own question is in
 * the HTML — this page is frequently the first impression of a brand, and a
 * flash of empty card before it loads is not one worth making.
 */

/** What the public endpoint adds to a form beyond `ReviewFormDto`. */
export type PublicFormResponse = {
  /*
    `directTo` is newer than the published `@flexbyt/shared`, so it is added
    here until the package carries it: set when the form sends reviewers to an
    external site and exactly one is connected.
  */
  form: ReviewFormDto & { directTo?: string | null };
  plan: string;
  platform: string | null;
  business: {
    siteId: string;
    name: string;
    website: string;
    logo: string;
  } | null;
};

export class FormNotAvailable extends Error {}

/**
 * The event and testimonial options, read defensively.
 *
 * Newer than the `@flexbyt/shared` this site installs, so they are typed here
 * rather than on `ReviewFormDto` — and an API that predates them sends none,
 * which must read as the form everybody had before: no role, no headshot. Fold into the shared type when the package is republished.
 */
export type FormOptions = {
  askRole: 'off' | 'optional' | 'required';
  askPhoto: boolean;
  roleLabel: string;
  rolePlaceholder: string;
  photoLabel: string;
};

export function formOptions(form: ReviewFormDto): FormOptions {
  const about = form.userInfo as { askRole?: string; askPhoto?: boolean };
  const t = form.translation as {
    yourRoleLabel?: string;
    yourRolePlaceholder?: string;
    yourPhotoLabel?: string;
  };
  const askRole =
    about.askRole === 'optional' || about.askRole === 'required'
      ? about.askRole
      : 'off';

  return {
    askRole,
    askPhoto: about.askPhoto === true,
    roleLabel: t.yourRoleLabel || 'Role & company',
    rolePlaceholder: t.yourRolePlaceholder || 'e.g. Founder, Acme Inc.',
    photoLabel: t.yourPhotoLabel || 'Your photo (optional)',
  };
}

function apiUrl(path: string): string {
  return new URL(path, site.api).toString();
}

/**
 * Fetch one form by its slug.
 *
 * A missing form throws `FormNotAvailable` so the route can answer 404, and any
 * other failure throws too. This is the *only* thing on the page — unlike the
 * testimonials on the homepage, there is no degraded version of it worth
 * rendering, and a card that says "we'd love your thoughts" over a form that
 * cannot submit is worse than an honest error.
 */
export async function fetchReviewForm(
  slug: string,
  { signal }: { signal?: AbortSignal } = {}
): Promise<PublicFormResponse> {
  const response = await fetch(
    apiUrl(`/api/public/forms/${encodeURIComponent(slug)}`),
    { signal, headers: { accept: 'application/json' } }
  );

  if (response.status === 404) throw new FormNotAvailable(slug);
  if (!response.ok) {
    throw new Error(`The form endpoint answered ${response.status}.`);
  }

  return (await response.json()) as PublicFormResponse;
}

/**
 * Who a review-request email was sent to.
 *
 * The link in that email carries `?email=<requestId>`, which lets the form
 * greet the customer by name and skip asking for an address we already have.
 *
 * Failure is silent and returns nothing. An expired or mistyped id must not
 * take the form down with it: the customer came here to write a review, and
 * asking them to type their own name is a far better outcome than a 404 on a
 * link a business printed.
 */
export async function fetchContact(
  id: string,
  { signal }: { signal?: AbortSignal } = {}
): Promise<{ contactName: string; contactEmail: string } | null> {
  try {
    const response = await fetch(
      apiUrl(`/api/public/forms/contacts/${encodeURIComponent(id)}`),
      { signal, headers: { accept: 'application/json' } }
    );
    if (!response.ok) return null;
    return (await response.json()) as {
      contactName: string;
      contactEmail: string;
    };
  } catch {
    return null;
  }
}

export type Submission = {
  rating: number;
  /** "CEO & President, UplingApp", when the form asks. */
  authorRole?: string;
  title: string;
  content: string;
  authorName: string;
  authorEmail: string;
  photos: File[];
  avatar: File | null;
  /**
   * The store's product this review is about, when the link said there was one.
   *
   * Set only by `/f/:slug?product=<id>`, which is the link the product widget
   * builds from the product page it is running on. Every other way into this
   * form — the popup widget, an emailed request, a QR code on a receipt — is
   * about the business rather than any one thing it sells, and leaves it empty.
   */
  productId?: string;
  /**
   * The review request this submission followed, from `/f/:slug?email=<id>`.
   *
   * Sent under its own name rather than `email` — that field already carries
   * the *reviewer's* address below, under the hosted form's legacy field
   * name, and the two must not collide.
   */
  requestId?: string;
};

/**
 * Send the review.
 *
 * `multipart/form-data` rather than JSON because of the photos, and under the
 * legacy field names (`name`, `email`, `message`) that the endpoint already
 * accepts alongside its own — see `normaliseSubmission` on the API. Sending the
 * names it prefers would work equally well; sending the ones the hosted form
 * has always sent means this page and the old one are interchangeable while
 * both exist.
 *
 * The slug travels in the URL, not the body. The person filling this in
 * followed a link, and the link is the only statement of which form this is —
 * a slug in the body could disagree with it.
 */
export async function submitReview(
  slug: string,
  submission: Submission,
  { signal }: { signal?: AbortSignal } = {}
): Promise<void> {
  const body = new FormData();

  body.append('rating', String(submission.rating));
  body.append('title', submission.title);
  body.append('message', submission.content);
  body.append('name', submission.authorName);
  body.append('email', submission.authorEmail);
  if (submission.authorRole) body.append('authorRole', submission.authorRole);

  // Only when there is one. An empty `product` field would still be a field,
  // and the endpoint would upsert a product row with an empty store id.
  if (submission.productId) body.append('productId', submission.productId);
  if (submission.requestId) body.append('requestId', submission.requestId);

  if (submission.avatar) body.append('reviewerAvatar', submission.avatar);
  for (const photo of submission.photos) body.append('reviewPhotos', photo);

  const response = await fetch(
    apiUrl(`/api/public/forms/${encodeURIComponent(slug)}/submit`),
    { method: 'POST', body, signal }
  );

  if (!response.ok) {
    /*
      The endpoint answers a 400 with `{ errors: { field: message } }` from its
      Zod schema. Surfacing the first of them beats "something went wrong" for
      the one case that actually happens: a review under the minimum length.
    */
    const message = await firstError(response);
    throw new Error(message);
  }
}

async function firstError(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as {
      message?: string;
      errors?: Record<string, string>;
    };
    const field = body.errors ? Object.values(body.errors)[0] : undefined;
    return field ?? body.message ?? 'Your review could not be sent.';
  } catch {
    return 'Your review could not be sent.';
  }
}

/**
 * Whether the hosted page carries the ReviewsJet badge.
 *
 * ---------------------------------------------------------------------------
 * Only Free is marked
 * ---------------------------------------------------------------------------
 * Removing the mark is one of the things a paid plan buys — the partners and
 * agencies pages both say so in as many words — and this page is the one place
 * a customer's customer ever sees us. Leaving our badge under a paying
 * business's own question is the promise being broken in the single most
 * visible spot there is.
 *
 * This is `planRemovesBranding` from `@flexbyt/shared` (`widget-settings.ts`),
 * inverted, and it is written out rather than imported for the same reason
 * `ReviewsJetBadge` is mirrored into this repo: it is not in the published
 * `1.0.1` this site installs. Replace both together —
 *
 *     import { planRemovesBranding } from '@flexbyt/shared';
 *
 * — and the expression here goes with them. It is copied exactly, so the two
 * cannot disagree in the meantime.
 *
 * ---------------------------------------------------------------------------
 * What counts as paid
 * ---------------------------------------------------------------------------
 * Any non-empty plan string other than `free`: plan ids (`professional`,
 * `advanced`) and the public API's tiers (`pro`, `advanced`) alike, and
 * anything this build has never heard of. That last case is deliberate and is
 * why the test is not `planTier(plan) !== 'free'` — an unrecognised id is far
 * more likely to be a tier added on the API side than a free account, and the
 * two mistakes are not equal. One costs us a badge; the other puts a badge on
 * a customer who paid to be rid of it.
 *
 * A missing plan is left showing the mark. The endpoint always sends one, so
 * absence means a workspace with nothing recorded, which has bought nothing.
 */
export function showsBranding(plan: string | null | undefined): boolean {
  return !(Boolean(plan) && plan !== 'free');
}
